import { Injectable } from '@angular/core';

import { Validators } from '../classes/validators.class';
import { CoreValidators } from '../validators';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';

@Injectable()
export class ValidationService {

    /**
     * The hooks that should be executed before save.
     *
     * @type {Array}
     */
    protected hooksBefore: Array<any> = [];

    /**
     * The hooks that should be executed after save.
     *
     * @type {Array}
     */
    protected hooksAfter: Array<any> = [];

    /**
     * The validators that should be registered.
     *
     * @type {Validators}
     */
    public validators: Validators = new Validators;

    /**
     * The BehaviorSubject observable instance we'll use for running the hooks.
     */
    protected observable: BehaviorSubject<any>;

    /**
     * The BehaviorSubject observable instance we'll use for running the hooks.
     */
    protected observableAfter: BehaviorSubject<any>;

    /**
     * Indicates if the before or after hooks are executed.
     */
    protected stage = 'before';

    /**
     * Constructor.
     */
    public constructor() {
        // Register core validators.
        this.validators.register(CoreValidators);
    }

    /**
     * Add a new validation hook to the validation.
     *
     * @param {(resolve: any, reject: any) => void} hook
     * @param execution
     */
    public addHook(hook: (resolve: any, reject: any) => void, execution: string = 'before') {
        if (execution === 'before') {
            this.hooksBefore.push(hook);
        } else if (execution === 'after') {
            this.hooksAfter.push(hook);
        }
    }

    /**
     * Add a new validation hook to the validation.
     *
     * @param {(resolve: any, reject: any) => void} hook
     */
    public before(hook: (resolve: any, reject: any) => void) {
        this.addHook(hook, 'before');
    }

    /**
     * Add a new validation hook to the validation.
     *
     * @param {(resolve: any, reject: any) => void} hook
     */
    public after(hook: (resolve: any, reject: any) => void) {
        this.addHook(hook, 'after');
    }

    /**
     * Run all the registered hooks.
     *
     * @param {(data?: any) => any} resolve
     * @param {(error?: any) => any} reject
     */
    public run(resolve: (data?: any) => any, reject: (error?: any) => any) {
        this.observable = new BehaviorSubject(0);

        this.observable.subscribe((index) => {
            // We'll check if there's a following hook. If so we'll run the next hook and
            // if not we'll mark the run as completed.
            if (this.hooksBefore[index]) {
                this.runHook(this.hooksBefore[index], index);
            } else {
                this.observable.complete();

                // We'll run the after hooks.
                this.observableAfter = new BehaviorSubject(0);

                this.observableAfter.subscribe((index) => {
                    if (this.hooksAfter[index]) {
                        this.runHook(this.hooksAfter[index], index);
                    } else {
                        this.observableAfter.complete();
                    }
                }, (error) => {
                    reject(error);
                }, () => {
                });
            }
        }, (error) => {
            reject(error);
        }, () => {
            // Here all hooks would've ran so we'll run the resolve callback.
            resolve();
        });
    }

    /**
     * Run a single hook.
     *
     * @param {(resolve: () => void, reject: (error?: any) => void) => void} hook
     * @param {number} index
     */
    public runHook(hook: (resolve: () => void, reject: (error?: any) => void) => void, index: number) {
        if (typeof hook === 'function') {
            hook(() => {
                // Run next hook if the previous successfully ran.
                this.observable.next(index + 1);
            }, (error) => {
                this.observable.error(error);
            });
        } else {
            this.observable.next(index + 1);
        }
    }

}




// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/forms/form/services/validation.service.ts